import React, { useEffect, useRef } from 'react';

/**
 * Subtle static honeycomb field for Infra (§03 section background).
 * No moving “light”; fixed low-contrast grid so the pillar diagram stays the focal animation.
 */
export function InfraHexMeshCanvas({ className }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const hexCorners = (cx, cy, R) => {
      const pts = [];
      for (let i = 0; i < 6; i += 1) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        pts.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]);
      }
      return pts;
    };

    const drawHex = (cx, cy, R, stroke, lw) => {
      const pts = hexCorners(cx, cy, R);
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < 6; i += 1) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.closePath();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = lw;
      ctx.stroke();
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      if (w < 1 || h < 1) return;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const drawField = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w <= 0 || h <= 0) return;

      ctx.clearRect(0, 0, w, h);

      const R = Math.max(12, Math.min(w, h) * 0.034);
      const dx = Math.sqrt(3) * R;
      const dy = 1.5 * R;
      const focusX = w * 0.52;
      const focusY = h * 0.45;

      for (let row = -2; row < h / dy + 4; row += 2) {
        for (let col = -2; col < w / dx + 4; col += 2) {
          const ox = (row % 2) * (dx / 2);
          const cx = col * dx + ox;
          const cy = row * dy;
          const dist = Math.hypot(cx - focusX, cy - focusY);
          const wave = Math.sin(dist * 0.0065 + col * 0.07 + row * 0.05);
          const pulse = 0.45 + 0.35 * (0.5 + 0.5 * wave);
          const falloff = Math.min(1, dist / (Math.min(w, h) * 0.58));
          const a = (0.022 + pulse * 0.042) * (1 - falloff * 0.5);
          const rTint = falloff * 0.28;
          const stroke = `rgba(${Math.round(210 - rTint * 35)}, ${Math.round(125 - rTint * 55)}, ${Math.round(75 + rTint * 30)}, ${a})`;
          drawHex(cx, cy, R * 0.92, stroke, 0.65);
        }
      }
    };

    const paint = () => {
      drawField();
    };

    const onResize = () => {
      resize();
      paint();
    };

    resize();
    paint();

    const ro = new ResizeObserver(onResize);
    ro.observe(canvas.parentElement);

    return () => {
      ro.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
